import {
  ZAD_SALES_HISTORY_MAX_RANGE_DAYS,
  ZAD_SALES_HISTORY_TZ,
  ZadSalesHistoryCustomerKind,
  ZadSalesHistoryDiscountFilter,
  ZadSalesHistoryPeriod,
  ZadSalesHistoryPreset,
  ZadSalesHistoryProductSortField,
  ZadSalesHistoryQuery,
  ZadSalesHistorySaleSortField,
  ZadSalesHistorySummary,
} from './salesHistoryTypes'
import {formatZadTnd} from './productHelpers'
import {hasZadFinancialAccess} from '../../../roles'
import {readZadSaleSettlement} from './saleSettlementHelpers'

export const ZAD_SALES_HISTORY_ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

export const ZAD_SALES_HISTORY_PRODUCT_SORTS: ZadSalesHistoryProductSortField[] = [
  'netSales',
  'quantitySold',
  'grossSales',
  'totalDiscount',
  'productName',
  'returnValue',
  'netCommercialValue',
]

export const ZAD_SALES_HISTORY_SALE_SORTS: ZadSalesHistorySaleSortField[] = [
  'saleDate',
  'saleNumber',
  'netTotal',
  'gross',
  'quantity',
]

export function canShowZadSalesHistoryProfit(role?: string | number | null): boolean {
  return hasZadFinancialAccess(role)
}

export function zadTunisYmd(value: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ZAD_SALES_HISTORY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(value)
}

export function isZadIsoDate(value: string | null | undefined): boolean {
  return Boolean(value && ZAD_SALES_HISTORY_ISO_DATE.test(value))
}

export function formatZadIsoDateDisplay(value: string | null | undefined): string {
  if (!value) return '—'
  const match = ZAD_SALES_HISTORY_ISO_DATE.exec(value)
  if (!match) return value
  return `${match[3]}/${match[2]}/${match[1]}`
}

export function formatZadTunisTime(value: string | Date | null | undefined): string {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('fr-TN', {
    timeZone: ZAD_SALES_HISTORY_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

function shiftTunisYmd(ymd: string, days: number): string {
  const [year, month, day] = ymd.split('-').map(Number)
  const utc = Date.UTC(year, month - 1, day + days, 12)
  return zadTunisYmd(new Date(utc))
}

function thisMonthStart(ymd: string): string {
  return `${ymd.slice(0, 7)}-01`
}

export function defaultZadSalesHistoryPeriod(now: Date = new Date()): ZadSalesHistoryPeriod {
  const today = zadTunisYmd(now)
  return {
    preset: 'today',
    mode: 'date',
    date: today,
    from: today,
    to: today,
  }
}

export function periodFromZadSalesHistoryPreset(
  preset: ZadSalesHistoryPreset,
  now: Date = new Date(),
  custom?: {from: string; to: string}
): ZadSalesHistoryPeriod {
  const today = zadTunisYmd(now)
  if (preset === 'yesterday') {
    const yesterday = shiftTunisYmd(today, -1)
    return {preset, mode: 'date', date: yesterday, from: yesterday, to: yesterday}
  }
  if (preset === 'last7') {
    return {preset, mode: 'range', from: shiftTunisYmd(today, -6), to: today}
  }
  if (preset === 'last30') {
    return {preset, mode: 'range', from: shiftTunisYmd(today, -29), to: today}
  }
  if (preset === 'thisMonth') {
    return {preset, mode: 'range', from: thisMonthStart(today), to: today}
  }
  if (preset === 'custom') {
    const from = custom?.from && isZadIsoDate(custom.from) ? custom.from : today
    const to = custom?.to && isZadIsoDate(custom.to) ? custom.to : today
    return {preset: 'custom', mode: 'range', from, to}
  }
  return defaultZadSalesHistoryPeriod(now)
}

export function inclusiveDayCount(from: string, to: string): number {
  const a = Date.parse(`${from}T12:00:00.000Z`)
  const b = Date.parse(`${to}T12:00:00.000Z`)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0
  return Math.floor((b - a) / 86400000) + 1
}

export function validateZadSalesHistoryRange(from: string, to: string): string | null {
  if (!isZadIsoDate(from) || !isZadIsoDate(to)) return 'INVALID_DATE'
  if (from > to) return 'FROM_AFTER_TO'
  if (inclusiveDayCount(from, to) > ZAD_SALES_HISTORY_MAX_RANGE_DAYS) return 'RANGE_TOO_LONG'
  return null
}

export function isSingleDayPeriod(period: ZadSalesHistoryPeriod): boolean {
  return period.from === period.to
}

export function defaultZadSalesHistoryQuery(now: Date = new Date()): ZadSalesHistoryQuery {
  const period = defaultZadSalesHistoryPeriod(now)
  return {
    period,
    customerKind: '',
    discount: 'ANY',
    productPageNumber: 1,
    productPageCount: 20,
    productSortField: 'netSales',
    productSortOrder: 'desc',
    salesPageNumber: 1,
    salesPageCount: 20,
    salesSortField: 'saleDate',
    salesSortOrder: 'desc',
    requestKey: `today:${period.date}`,
  }
}

export function buildZadSalesHistoryPeriodParams(period: ZadSalesHistoryPeriod): Record<string, string> {
  if (period.mode === 'date' && period.date && isZadIsoDate(period.date)) {
    return {date: period.date}
  }
  return {from: period.from, to: period.to}
}

export function buildZadSalesHistoryQueryString(
  query: Pick<ZadSalesHistoryQuery, 'period' | 'customerKind' | 'discount'> & {
    pageNumber?: number
    pageCount?: number
    sortField?: string
    sortOrder?: string
  }
): string {
  const params = new URLSearchParams()
  const period = buildZadSalesHistoryPeriodParams(query.period)
  Object.entries(period).forEach(([key, value]) => params.set(key, value))
  if (query.customerKind === 'WALK_IN' || query.customerKind === 'REGISTERED') {
    params.set('customerKind', query.customerKind)
  }
  if (query.discount === 'WITH_DISCOUNT' || query.discount === 'WITHOUT_DISCOUNT') {
    params.set('discount', query.discount)
  }
  if (query.pageNumber) params.set('pageNumber', String(query.pageNumber))
  if (query.pageCount) params.set('pageCount', String(query.pageCount))
  if (query.sortField) params.set('sortField', query.sortField)
  if (query.sortOrder) params.set('sortOrder', query.sortOrder)
  return params.toString()
}

export function parseZadSalesHistorySearchParams(
  search: URLSearchParams,
  now: Date = new Date()
): ZadSalesHistoryQuery {
  const defaults = defaultZadSalesHistoryQuery(now)
  const date = search.get('date') || ''
  const from = search.get('from') || ''
  const to = search.get('to') || ''
  let period = defaults.period
  if (isZadIsoDate(date)) {
    period = {preset: 'custom', mode: 'date', date, from: date, to: date}
    const today = zadTunisYmd(now)
    const yesterday = shiftTunisYmd(today, -1)
    if (date === today) period = {...period, preset: 'today'}
    else if (date === yesterday) period = {...period, preset: 'yesterday'}
  } else if (isZadIsoDate(from) && isZadIsoDate(to) && !validateZadSalesHistoryRange(from, to)) {
    const last7 = periodFromZadSalesHistoryPreset('last7', now)
    const last30 = periodFromZadSalesHistoryPreset('last30', now)
    const thisMonth = periodFromZadSalesHistoryPreset('thisMonth', now)
    period = {preset: 'custom', mode: 'range', from, to}
    if (from === last7.from && to === last7.to) period.preset = 'last7'
    else if (from === last30.from && to === last30.to) period.preset = 'last30'
    else if (from === thisMonth.from && to === thisMonth.to) period.preset = 'thisMonth'
  }

  const discountRaw = search.get('discount') || ''
  const discount: ZadSalesHistoryDiscountFilter =
    discountRaw === 'WITH_DISCOUNT' || discountRaw === 'WITHOUT_DISCOUNT' ? discountRaw : 'ANY'
  const kindRaw = search.get('customerKind') || ''
  const customerKind: ZadSalesHistoryCustomerKind =
    kindRaw === 'WALK_IN' || kindRaw === 'REGISTERED' ? kindRaw : ''
  const productSort = search.get('sortField') as ZadSalesHistoryProductSortField
  const productSortField = ZAD_SALES_HISTORY_PRODUCT_SORTS.includes(productSort)
    ? productSort
    : defaults.productSortField
  const productSortOrder = search.get('sortOrder') === 'asc' ? 'asc' : 'desc'

  return {
    ...defaults,
    period,
    discount,
    customerKind,
    productPageNumber: Math.max(1, Number(search.get('productPage')) || 1),
    productPageCount: Math.max(1, Number(search.get('productCount')) || defaults.productPageCount),
    productSortField,
    productSortOrder,
    salesPageNumber: Math.max(1, Number(search.get('salesPage')) || 1),
    salesPageCount: Math.max(1, Number(search.get('salesCount')) || defaults.salesPageCount),
    requestKey: `${period.mode}:${period.date || ''}:${period.from}:${period.to}:${discount}:${customerKind}`,
  }
}

export function zadSalesHistorySearchFromQuery(
  query: ZadSalesHistoryQuery,
  current?: URLSearchParams | null
): URLSearchParams {
  const params = new URLSearchParams()
  const period = buildZadSalesHistoryPeriodParams(query.period)
  Object.entries(period).forEach(([key, value]) => params.set(key, value))
  if (query.customerKind) params.set('customerKind', query.customerKind)
  if (query.discount && query.discount !== 'ANY') params.set('discount', query.discount)
  if (query.productPageNumber > 1) params.set('productPage', String(query.productPageNumber))
  if (query.productSortField !== 'netSales') params.set('sortField', query.productSortField)
  if (query.productSortOrder !== 'desc') params.set('sortOrder', query.productSortOrder)
  if (query.salesPageNumber > 1) params.set('salesPage', String(query.salesPageNumber))
  if (current) {
    ;(['tab', 'followup', 'dossier', 'saleId'] as const).forEach((key) => {
      const value = current.get(key)
      if (value) params.set(key, value)
    })
  }
  return params
}

export function formatZadReductionTnd(amount: number | null | undefined): string {
  const numeric = Number(amount) || 0
  if (numeric === 0) return formatZadTnd(0)
  return `−${formatZadTnd(Math.abs(numeric))}`
}

export function formatZadSignedTnd(amount: number | null | undefined): string {
  const numeric = Number(amount)
  if (!Number.isFinite(numeric) || numeric === 0) return formatZadTnd(0)
  if (numeric < 0) return `−${formatZadTnd(Math.abs(numeric))}`
  return formatZadTnd(numeric)
}

export function formatZadHistoryQuantity(value: number | null | undefined): string {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '0'
  if (Number.isInteger(numeric)) return String(numeric)
  return String(numeric)
}

export type ZadSalesHistoryEmptyKind = 'commercial' | 'return-only' | 'money-only' | 'activity'

export function historyCollectedTotal(summary?: ZadSalesHistorySummary | null): number {
  if (summary?.collectedAmount != null && Number.isFinite(Number(summary.collectedAmount))) {
    return Number(summary.collectedAmount) || 0
  }
  return Number(summary?.moneyIn?.total) || 0
}

export function historyRefundedTotal(summary?: ZadSalesHistorySummary | null): number {
  if (summary?.refundedAmount != null && Number.isFinite(Number(summary.refundedAmount))) {
    return Number(summary.refundedAmount) || 0
  }
  return Number(summary?.refundsOut?.total) || 0
}

export function historyNetCashCollected(summary?: ZadSalesHistorySummary | null): number {
  if (summary?.netCashCollected != null && Number.isFinite(Number(summary.netCashCollected))) {
    return Number(summary.netCashCollected) || 0
  }
  return historyCollectedTotal(summary) - historyRefundedTotal(summary)
}

export function historyOutstandingReceivables(summary?: ZadSalesHistorySummary | null): number {
  if (summary?.outstandingReceivables == null) return Number.NaN
  return Number(summary.outstandingReceivables) || 0
}

export function classifyZadSalesHistoryEmpty(summary: ZadSalesHistorySummary | null): ZadSalesHistoryEmptyKind {
  if (!summary) return 'activity'
  const salesCount = Number(summary.salesCount) || 0
  const returnValue = Number(summary.returnValue) || 0
  const moneyIn = historyCollectedTotal(summary)
  if (salesCount === 0 && returnValue === 0 && moneyIn > 0) return 'money-only'
  if (salesCount === 0 && returnValue > 0) return 'return-only'
  if (salesCount === 0 && returnValue === 0) return 'commercial'
  return 'activity'
}

export function historyWalkInLabel(
  kind?: string | null,
  name?: string | null
): {isWalkIn: boolean; label: string | null} {
  if (kind === 'WALK_IN') {
    const trimmed = name && String(name).trim() ? String(name).trim() : null
    return {isWalkIn: true, label: trimmed}
  }
  if (name && String(name).trim()) return {isWalkIn: false, label: String(name).trim()}
  return {isWalkIn: false, label: null}
}

export function shouldKeepDailyZeroBuckets(daily: Array<{date: string; netSales?: number}>) {
  return daily
}

export function historySaleSettlement(row?: unknown) {
  return readZadSaleSettlement(row)
}

export function historySettlementMessageId(status?: string | null): string {
  if (status === 'PARTIAL') return 'ZAD.SALES_HISTORY.SETTLEMENT_PARTIAL'
  if (status === 'SETTLED') return 'ZAD.SALES_HISTORY.SETTLEMENT_SETTLED'
  return 'ZAD.SALES_HISTORY.SETTLEMENT_UNPAID'
}
